import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Ozempic — semaglutide, the weekly pen a patient may be on for months
 * before a cycle, and the first dial-a-dose pen here whose label says to
 * check the flow of every new pen and to count to six.
 *
 * ### One label, two languages
 *
 * The EMA product information carries the pen's instructions for use in
 * English and in French, identical step for step: check the name and the
 * coloured label; clear and colourless through the window; a new needle,
 * paper tab off, straight on and tight, both caps off and the outer one
 * kept; **check the flow with each new pen** at the flow-check symbol until
 * a drop appears (up to six tries, then a new needle, then a new pen);
 * turn the selector until the counter stops at the dose and never count
 * the clicks; needle in as shown, counter visible, button held until 0 and
 * **a slow count to six**; blood pressed lightly; needle into its outer cap
 * on a flat surface, unscrewed, binned; pen cap back on. The English copy is
 * the English leaflet's and the French the French notice's.
 *
 * ### What the walk leaves out, and why
 *
 * The leaflet names three places — front of the thighs, front of the waist,
 * upper arm. The coach's site question knows the abdomen and the thigh (the
 * camera can outline one and mirror the other); the upper arm is not
 * offered and the fiche says so. No angle is stated and none is drawn.
 *
 * ### Not a fertility drug
 *
 * Semaglutide is stopped before a pregnancy is attempted, and the content
 * entry says so in its first sentence; this walk is the hands only, for the
 * months before. The indication is `metabolic`, never a trigger, never on a
 * cycle step. Dose: milligrams, and only the four strengths a pen can
 * select — a record in another unit or another number is asked about.
 *
 * Restated from `content/data/medications/glp1.json` (the semaglutide entry),
 * whose pen procedure was written from these two leaflets; the entry's own
 * "stop before a planned pregnancy" text is untouched.
 */

const OZEMPIC_BRANDS = [
  { name: "Ozempic", markets: ["EU", "GB"] },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's five-day rule, both languages. */
const MISSED_DOSE: ProtocolWarning = {
  id: "ozempic.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - If it is 5 days or less since you should have used it, inject it as soon as you remember, then inject your next dose as usual on your scheduled day. If it is more than 5 days, skip the missed dose and inject your next dose as usual on your scheduled day. Do not use a double dose to make up for a forgotten dose.",
    fr: "Dose oubliée - Si 5 jours maximum se sont écoulés depuis la date de l'injection oubliée, injectez la dose dès que vous y pensez, puis injectez la dose suivante comme d'habitude, le jour prévu. Si plus de 5 jours se sont écoulés, ne prenez pas la dose manquée et injectez la dose suivante comme d'habitude, le jour prévu. N'utilisez pas de dose double pour compenser la dose que vous avez oublié de prendre.",
  },
  sourceSegment:
    "⚠ Missed dose - If it is 5 days or less since you should have used it, inject it as soon as you remember, then inject your next dose as usual on your scheduled day. If it is more than 5 days, skip the missed dose and inject your next dose as usual on your scheduled day. Do not use a double dose to make up for a forgotten dose.",
};

/** The leaflet's rare line, with breathing in it, routed to emergency. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "ozempic.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Breathing problems, swelling of the face, lips, tongue or throat with difficulty swallowing, or a fast heartbeat are signs of a severe allergic reaction: get emergency help straight away.",
    fr: "Aide urgente - Des problèmes respiratoires, un gonflement du visage, des lèvres, de la langue ou de la gorge avec des difficultés à avaler, ou des battements de cœur rapides sont les signes d'une réaction allergique sévère : appelez les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Breathing problems, swelling of the face, lips, tongue or throat with difficulty swallowing, or a fast heartbeat are signs of a severe allergic reaction: get emergency help straight away.",
};

/** Acute pancreatitis, the leaflet's "see a doctor immediately". */
const PANCREATITIS: ProtocolWarning = {
  id: "ozempic.pancreatitis",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Stomach and back pain - Severe pain in the stomach and back which does not go away can be an inflamed pancreas: stop and see a doctor immediately.",
    fr: "Douleur à l'estomac et au dos - Une sévère douleur persistante au niveau de l'estomac et du dos peut être une inflammation du pancréas : arrêtez et consultez immédiatement un médecin.",
  },
  sourceSegment:
    "⚠ Stomach and back pain - Severe pain in the stomach and back which does not go away can be an inflamed pancreas: stop and see a doctor immediately.",
};

/**
 * "The front of your thighs, the front of your waist (abdomen), or your upper
 * arm." The arm is not offered — see the file comment.
 */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Choose the site - Inject under the skin, never into a vein or a muscle, at the front of your waist: here, on your abdomen. Your doctor or nurse will have shown you how to insert the needle.",
      fr: "Choisir le site - Injectez sous la peau, jamais directement dans une veine ou dans un muscle, sur le ventre : ici, sur l'abdomen. Votre médecin ou votre infirmière vous aura montré comment insérer l'aiguille.",
    },
  },
  {
    id: "thigh",
    label: { en: "Front of thigh", fr: "Dessus de la cuisse" },
    copy: {
      en: "Choose the site - Inject under the skin, never into a vein or a muscle, here, on the front of your thigh. Your doctor or nurse will have shown you how to insert the needle.",
      fr: "Choisir le site - Injectez sous la peau, jamais directement dans une veine ou dans un muscle, ici, sur le dessus de la cuisse. Votre médecin ou votre infirmière vous aura montré comment insérer l'aiguille.",
    },
  },
];

export const OZEMPIC_PROTOCOL: InjectionProtocol = {
  medicationSlug: "ozempic",
  brands: OZEMPIC_BRANDS,
  // Not "semaglutide": Wegovy and Rybelsus are semaglutide too, with a pen
  // and a tablet of their own.
  aliases: [],
  indication: "metabolic",
  // Each pen selects one dose and nothing else; a record in another number
  // is asked about, never rounded.
  dose: { unit: "mg", strengths: [0.25, 0.5, 1, 2] },
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/glp1.json#semaglutide",
  sites: SITES,
  steps: [
    {
      id: "ozempic.pen.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Check the name and the coloured label of your pen to make sure that it contains {brand} at your dose{dose}; this is especially important if you use more than one type of injectable medicine. Pull off the pen cap and look through the pen window: the solution must be clear and colourless, and you must not use the pen if it looks cloudy or coloured. Lay out a new needle and a sharps container. {brand} is injected once a week, on the same day each week if possible, at any time of the day, with or without a meal.",
        fr: "Préparer - Contrôlez le nom et l'étiquette colorée de votre stylo pour vous assurer qu'il contient bien {brand} à votre dose{dose} ; ceci est particulièrement important si vous utilisez plus d'un type de médicament injectable. Retirez le capuchon du stylo et regardez à travers la fenêtre du stylo : la solution doit être limpide et incolore, et vous ne devez pas utiliser le stylo si elle paraît trouble ou colorée. Disposez une aiguille neuve et un collecteur. {brand} s'injecte une fois par semaine, le même jour chaque semaine si possible, à tout moment de la journée, au cours ou en dehors des repas.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "ozempic.pen.attach-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Attach the needle - Take a new needle and check that the paper tab and the outer needle cap are not damaged; if they are, use another new needle. Tear off the paper tab, push the needle straight onto the pen and turn until it is on tight. Pull off the outer needle cap and keep it for later, then pull off the inner needle cap and throw it away; never try to put the inner cap back on, you may stick yourself. A drop of solution may appear at the needle tip: this is normal. Always use a new needle for each injection, and never a bent or damaged one.",
        fr: "Fixer l'aiguille - Prenez une aiguille neuve et vérifiez que la languette en papier et le capuchon externe de l'aiguille ne sont pas endommagés ; s'ils le sont, utilisez une autre aiguille neuve. Retirez la languette en papier, insérez l'aiguille bien droite sur le stylo et tournez jusqu'à ce qu'elle soit solidement fixée. Retirez le capuchon externe de l'aiguille et conservez-le pour plus tard, puis retirez le capuchon interne de l'aiguille et jetez-le ; n'essayez jamais de remettre le capuchon interne, vous risqueriez de vous piquer. Une goutte de solution peut apparaître à la pointe de l'aiguille : c'est normal. Utilisez toujours une aiguille neuve pour chaque injection, et jamais une aiguille pliée ou endommagée.",
      },
    },
    {
      // The one step a pen in use skips, said in its first sentence.
      id: "ozempic.pen.check-for-a-droplet",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Check the flow with each new pen - If your pen is already in use, there is nothing to check here. Before the first injection with a new pen, turn the dose selector to the flow check symbol just past 0, lined up with the dose pointer. Hold the pen with the needle pointing up, then press and hold the dose button until the dose counter returns to 0: a drop of solution must appear at the needle tip. If no drop appears, repeat this check up to 6 times; if there is still no drop, change the needle and check once more, and if a drop still does not appear, dispose of the pen and use a new one. Without the drop you would inject no medicine, even though the dose counter moves.",
        fr: "Vérifier l'écoulement avec chaque stylo neuf - Si votre stylo est déjà en cours d'utilisation, il n'y a rien à vérifier ici. Avant la première injection avec un stylo neuf, tournez le sélecteur de dose sur le symbole de contrôle de l'écoulement juste après 0, aligné avec l'indicateur de dose. Maintenez le stylo avec l'aiguille pointée vers le haut, puis maintenez le bouton de dose enfoncé jusqu'à ce que le compteur de dose revienne à 0 : une goutte de solution doit apparaître à la pointe de l'aiguille. Si aucune goutte n'apparaît, répétez cette vérification 6 fois maximum ; s'il n'y a toujours pas de goutte, remplacez l'aiguille et vérifiez encore une fois, et si aucune goutte n'apparaît toujours, jetez le stylo et utilisez un stylo neuf. Sans la goutte, vous n'injecteriez pas de médicament, même si le compteur de dose bouge.",
      },
    },
    {
      id: "ozempic.pen.inspect-dose-window",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Select your dose - Turn the dose selector until the dose counter stops and shows your dose{dose}; this pen can select that dose only. Do not count the pen clicks: only the dose counter and the dose pointer show that the dose has been selected, and the number must line up precisely with the dose pointer before you inject.",
        fr: "Sélectionner votre dose - Tournez le sélecteur de dose jusqu'à ce que le compteur de dose s'arrête et indique votre dose{dose} ; ce stylo ne permet de sélectionner que cette dose. Ne comptez pas les clics du stylo : seuls le compteur de dose et l'indicateur de dose montrent que la dose a été sélectionnée, et le nombre doit être parfaitement aligné avec l'indicateur de dose avant d'injecter.",
      },
    },
    {
      id: "ozempic.pen.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // No angle in the label — "as your doctor or nurse has shown you" —
      // so none is said and the figure draws none.
      id: "ozempic.pen.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Insert the needle into your skin as your doctor or nurse has shown you, making sure you can see the dose counter; do not cover it with your fingers, as this could interrupt the injection. Press and hold down the dose button and watch the dose counter return to 0, lined up with the dose pointer; you may hear or feel a click. Keep the button pressed with the needle in your skin and count slowly to 6. Then remove the needle from your skin and release the dose button; if blood appears, press lightly. If 0 does not appear in the dose counter after pressing continuously, the needle may be blocked or damaged and you have not received any medicine: change the needle and start again from the first step, selecting the full dose you need.",
        fr: "Injecter - Insérez l'aiguille dans votre peau comme votre médecin ou votre infirmière vous l'a montré, en vous assurant de pouvoir voir le compteur de dose ; ne le recouvrez pas avec vos doigts, cela pourrait interrompre l'injection. Appuyez sur le bouton de dose et maintenez-le enfoncé, et regardez le compteur de dose revenir à 0, aligné avec l'indicateur de dose ; vous pourrez entendre ou sentir un clic. Maintenez le bouton enfoncé avec l'aiguille dans votre peau et comptez lentement jusqu'à 6. Retirez ensuite l'aiguille de votre peau et relâchez le bouton de dose ; si du sang apparaît, appuyez légèrement. Si le compteur de dose n'affiche pas 0 alors que le bouton a été maintenu enfoncé, l'aiguille est peut-être bouchée ou endommagée et vous n'avez pas reçu de médicament : changez l'aiguille et recommencez depuis la première étape, en sélectionnant la totalité de la dose qu'il vous faut.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      id: "ozempic.pen.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Lead the needle tip into the outer needle cap on a flat surface, without touching the needle or the cap, push the cap completely on, unscrew the needle and put it in your sharps container; always remove the needle right after each injection. Put the pen cap back on to protect the solution from light, and note the date on the carton. An opened pen keeps for 6 weeks below 30 degrees Celsius or in the refrigerator; never inject it if it has been frozen or left in direct sunlight.",
        fr: "Après l'injection - Placez l'extrémité de l'aiguille à l'intérieur du capuchon externe de l'aiguille, sur une surface plane, sans toucher l'aiguille ni le capuchon, emboîtez complètement le capuchon, dévissez l'aiguille et jetez-la dans votre collecteur ; retirez toujours l'aiguille immédiatement après chaque injection. Remettez le capuchon sur votre stylo pour protéger la solution de la lumière, et notez la date sur l'étui. Un stylo entamé se conserve 6 semaines à une température ne dépassant pas 30 degrés Celsius ou au réfrigérateur ; ne l'injectez jamais s'il a été congelé ou exposé à la lumière directe du soleil.",
      },
      warnings: [PANCREATITIS],
    },
  ],
};
